/**
 * GA4 Server-Side Tracking Service
 *
 * Envia todos os eventos via Measurement Protocol (server-side)
 * Benefícios:
 * - Não é bloqueado por ad blockers
 * - Dados mais precisos
 * - Melhor atribuição de conversões
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// Configurações
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutos
const CLIENT_ID_KEY = 'ga4_client_id';
const SESSION_KEY = 'ga4_session';

class GA4TrackingService {
  constructor() {
    this.clientId = null;
    this.sessionId = null;
    this.sessionStart = null;
    this.trackFunction = null;
    this.initialized = false;
  }

  /**
   * Inicializa o serviço
   */
  init() {
    if (this.initialized) return;

    this.clientId = this._getOrCreateClientId();
    this._initSession();
    this.initialized = true;
  }

  /**
   * Obtém ou cria Client ID persistente
   * Formato: timestamp.random (similar ao GA4 cookie)
   */
  _getOrCreateClientId() {
    let clientId = localStorage.getItem(CLIENT_ID_KEY);

    if (!clientId) {
      // Tentar extrair do cookie _ga se existir (gtag client-side)
      const gaCookie = document.cookie
        .split(';')
        .find(c => c.trim().startsWith('_ga='));

      if (gaCookie) {
        // _ga=GA1.1.123456789.1234567890
        const parts = gaCookie.split('.');
        if (parts.length >= 4) {
          clientId = `${parts[2]}.${parts[3]}`;
        }
      }

      // Se não encontrou, gerar novo
      if (!clientId) {
        clientId = `${Date.now()}.${Math.floor(Math.random() * 1000000000)}`;
      }

      localStorage.setItem(CLIENT_ID_KEY, clientId);
    }

    return clientId;
  }

  /**
   * Gerencia sessão com timeout de 30 min
   */
  _initSession() {
    const stored = sessionStorage.getItem(SESSION_KEY);

    if (stored) {
      const session = JSON.parse(stored);
      const elapsed = Date.now() - session.lastActivity;

      if (elapsed < SESSION_TIMEOUT) {
        this.sessionId = session.id;
        this.sessionStart = session.start;
        this._updateSessionActivity();
        return;
      }
    }

    // Nova sessão
    this.sessionId = `${Date.now()}.${Math.floor(Math.random() * 1000000)}`;
    this.sessionStart = Date.now();
    this._updateSessionActivity();
  }

  _updateSessionActivity() {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      id: this.sessionId,
      start: this.sessionStart,
      lastActivity: Date.now()
    }));
  }

  /**
   * Envia evento para GA4 via Cloud Function
   */
  async _sendEvent(eventName, params = {}, userProperties = {}) {
    if (!this.initialized) this.init();

    try {
      if (!this.trackFunction) {
        const functions = getFunctions();
        this.trackFunction = httpsCallable(functions, 'trackGA4Event');
      }

      // Calcular engagement time (tempo desde início da sessão)
      const engagementTime = Math.min(
        Date.now() - this.sessionStart,
        SESSION_TIMEOUT
      );

      const result = await this.trackFunction({
        clientId: this.clientId,
        eventName,
        params: {
          session_id: this.sessionId,
          engagement_time_msec: engagementTime,
          page_location: window.location.href,
          page_title: document.title,
          page_referrer: document.referrer,
          ...params
        },
        userProperties
      });

      this._updateSessionActivity();
      return result.data;

    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ========== EVENTOS PADRÃO ==========

  /**
   * Page View - chamar em cada navegação
   */
  async trackPageView(pageTitle, pagePath) {
    return this._sendEvent('page_view', {
      page_title: pageTitle || document.title,
      page_location: window.location.origin + (pagePath || window.location.pathname)
    });
  }

  /**
   * Sign Up - quando usuário cria conta
   */
  async trackSignUp(method = 'email') {
    return this._sendEvent('sign_up', {
      method
    });
  }

  /**
   * Login - quando usuário faz login
   */
  async trackLogin(method = 'email') {
    return this._sendEvent('login', {
      method
    });
  }

  /**
   * View Item - quando visualiza um plano/produto
   */
  async trackViewItem(item) {
    return this._sendEvent('view_item', {
      currency: item.currency || 'BRL',
      value: item.price || 0,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: 'subscription',
        price: item.price,
        quantity: 1
      }]
    });
  }

  /**
   * Begin Checkout - quando inicia checkout
   */
  async trackBeginCheckout(item) {
    return this._sendEvent('begin_checkout', {
      currency: item.currency || 'BRL',
      value: item.price || 0,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: 'subscription',
        price: item.price,
        quantity: 1
      }]
    });
  }

  /**
   * Add Payment Info - quando preenche dados de pagamento
   */
  async trackAddPaymentInfo(item, paymentType = 'paypal') {
    return this._sendEvent('add_payment_info', {
      currency: item.currency || 'BRL',
      value: item.price || 0,
      payment_type: paymentType,
      items: [{
        item_id: item.id,
        item_name: item.name,
        item_category: 'subscription',
        price: item.price,
        quantity: 1
      }]
    });
  }

  /**
   * Purchase - quando compra é concluída
   * NOTA: Este é enviado server-side pelo PayPal webhook
   * Use apenas para tracking adicional client-side se necessário
   */
  async trackPurchase(transaction) {
    return this._sendEvent('purchase', {
      transaction_id: transaction.id,
      currency: transaction.currency || 'BRL',
      value: transaction.value,
      items: [{
        item_id: transaction.itemId,
        item_name: transaction.itemName,
        item_category: 'subscription',
        price: transaction.value,
        quantity: 1
      }]
    });
  }

  /**
   * Generate Lead - quando demonstra interesse
   */
  async trackGenerateLead(value = 0, currency = 'BRL') {
    return this._sendEvent('generate_lead', {
      currency,
      value
    });
  }

  /**
   * Evento customizado
   */
  async trackCustomEvent(eventName, params = {}) {
    return this._sendEvent(eventName, params);
  }

}

// Singleton
export const ga4Tracking = new GA4TrackingService();

// Exports de conveniência
export const trackPageView = (title, path) => ga4Tracking.trackPageView(title, path);
export const trackSignUp = (method) => ga4Tracking.trackSignUp(method);
export const trackLogin = (method) => ga4Tracking.trackLogin(method);
export const trackViewItem = (item) => ga4Tracking.trackViewItem(item);
export const trackBeginCheckout = (item) => ga4Tracking.trackBeginCheckout(item);
export const trackAddPaymentInfo = (item, type) => ga4Tracking.trackAddPaymentInfo(item, type);
export const trackPurchase = (tx) => ga4Tracking.trackPurchase(tx);
export const trackGenerateLead = (value, currency) => ga4Tracking.trackGenerateLead(value, currency);
export const trackCustomEvent = (name, params) => ga4Tracking.trackCustomEvent(name, params);
